import React, { useState, useEffect } from 'react';
import { CheckCircle, X, Mail, Phone, Calendar, Clock, MapPin, Users } from 'lucide-react';
import { format, parseISO, parse, subMinutes } from 'date-fns';
import { useFacility } from '../context/FacilityContext';
import { composeSlotLabel } from '../utils/slotName';

const googleMapUrl = (lat, lng) => {
  const la = parseFloat(lat);
  const lo = parseFloat(lng);
  if (!Number.isFinite(la) || !Number.isFinite(lo)) return null;
  const q = `${la},${lo}`;
  return `https://maps.google.com/?q=${encodeURIComponent(q)}`;
};

const Notification = ({ booking, onClose, onConfirm, matchDetails: matchDetailsProp }) => {
  const { activeFacility } = useFacility();
  const [isVisible, setIsVisible] = useState(true);
  const [whatsappStatus, setWhatsappStatus] = useState('');

  if (!isVisible || !booking) return null;

  // Calculate total amount correctly - check multiple sources
  const calculateTotalAmount = () => {
    // Priority 1: Direct booking.totalAmount
    if (booking.totalAmount !== undefined && booking.totalAmount !== null) {
      return parseFloat(booking.totalAmount);
    }
    // Priority 2: Calculate from baseAmount and discountAmount
    if (booking.baseAmount !== undefined && booking.baseAmount !== null) {
      const base = parseFloat(booking.baseAmount);
      const discount = parseFloat(booking.discountAmount || 0);
      return Math.max(0, base - discount);
    }
    // Priority 3: Check paymentDetails object
    if (booking.paymentDetails?.totalAmount !== undefined && booking.paymentDetails?.totalAmount !== null) {
      return parseFloat(booking.paymentDetails.totalAmount);
    }
    if (booking.paymentDetails?.baseAmount !== undefined && booking.paymentDetails?.baseAmount !== null) {
      const base = parseFloat(booking.paymentDetails.baseAmount);
      const discount = parseFloat(booking.paymentDetails.discountAmount || 0);
      return Math.max(0, base - discount);
    }
    // Priority 4: Fallback to price field
    return parseFloat(booking.price || 0);
  };

  const totalAmount = calculateTotalAmount();
  
  // Extract amounts from booking or paymentDetails - check multiple sources
  const baseAmount = (booking.baseAmount !== undefined && booking.baseAmount !== null)
    ? parseFloat(booking.baseAmount)
    : (booking.paymentDetails?.baseAmount !== undefined && booking.paymentDetails?.baseAmount !== null
      ? parseFloat(booking.paymentDetails.baseAmount)
      : (totalAmount > 0 ? totalAmount : 0));
  
  const discountAmount = (booking.discountAmount !== undefined && booking.discountAmount !== null)
    ? parseFloat(booking.discountAmount)
    : (booking.paymentDetails?.discountAmount !== undefined && booking.paymentDetails?.discountAmount !== null
      ? parseFloat(booking.paymentDetails.discountAmount)
      : 0);
  
  const advanceAmount = (booking.advanceAmount !== undefined && booking.advanceAmount !== null)
    ? parseFloat(booking.advanceAmount)
    : (booking.paymentDetails?.advanceAmount !== undefined && booking.paymentDetails?.advanceAmount !== null
      ? parseFloat(booking.paymentDetails.advanceAmount)
      : 0);
  
  const balanceAmount = (booking.balanceAmount !== undefined && booking.balanceAmount !== null)
    ? parseFloat(booking.balanceAmount)
    : Math.max(0, totalAmount - advanceAmount);
  
  // Extract contact information (handle both name and contactName)
  const contactName = booking.contactName || booking.name || booking.bookedBy || 'Guest';
  const contactPhone = booking.contactPhone || booking.bookedByPhone || '';
  const contactEmail = booking.contactEmail || '';

  const normalizePhoneForWhatsApp = (rawPhone) => {
    const digits = String(rawPhone || '').replace(/\D/g, '');
    if (!digits) return '';
    // Default to India country code for 10-digit local numbers.
    if (digits.length === 10) return `91${digits}`;
    return digits;
  };

  const bookingDateLabel = booking.date ? (() => {
    try {
      // Handle ISO date strings
      if (booking.date.includes('T')) {
        return format(parseISO(booking.date), 'dd MMM yyyy');
      }
      // Handle YYYY-MM-DD format
      const dateObj = new Date(booking.date);
      if (!isNaN(dateObj.getTime())) {
        return format(dateObj, 'dd MMM yyyy');
      }
      return booking.date;
    } catch (e) {
      return booking.date || 'N/A';
    }
  })() : 'N/A';

  const bookingTimeLabel = composeSlotLabel(booking.slotName, booking.timeSlot);

  // Parse slot start/end times and compute reporting time (30 minutes before start)
  const parseSlotTimes = (timeSlot) => {
    if (!timeSlot || typeof timeSlot !== 'string') {
      return { startLabel: 'N/A', endLabel: 'N/A', reportingLabel: 'N/A' };
    }
    const match = timeSlot.match(/(\d{1,2}:\d{2})\s*-\s*(\d{1,2}:\d{2})/);
    if (!match) {
      return { startLabel: timeSlot, endLabel: 'N/A', reportingLabel: 'N/A' };
    }
    try {
      const now = new Date();
      const start = parse(match[1], 'HH:mm', now);
      const end = parse(match[2], 'HH:mm', now);
      const reporting = subMinutes(start, 30);
      return {
        startLabel: format(start, 'h:mm a'),
        endLabel: format(end, 'h:mm a'),
        reportingLabel: format(reporting, 'h:mm a'),
      };
    } catch {
      return { startLabel: match[1], endLabel: match[2], reportingLabel: 'N/A' };
    }
  };

  const { startLabel, endLabel, reportingLabel } = parseSlotTimes(booking.timeSlot || '');

  // GA-008: a team block should only render when it carries real data. Single-
  // team bookings can leave the other side as an empty object, which otherwise
  // produced a blank "Team B / Captain / 0 players" card.
  const hasTeamData = (team) =>
    !!(team && (String(team.name || '').trim() || String(team.captain || '').trim()));
  const showTeamA = hasTeamData(booking.teamDetails?.teamA);
  const showTeamB = hasTeamData(booking.teamDetails?.teamB);
  const hasGuests = Array.isArray(booking.guestList) && booking.guestList.length > 0;

  // Friendly labels for venue, sport, ball, contact
  const venueName =
    booking.venue ||
    booking.groundName ||
    booking.serviceName ||
    'Ground';

  const sportType = (() => {
    const t = booking.serviceType || '';
    if (t === 'cricket_match') return 'Cricket Match';
    if (t === 'practice_net') return 'Practice Net';
    if (t === 'open_net') return 'Open Net';
    if (t === 'box_cricket') return 'Box Cricket';
    return booking.serviceName || 'Cricket';
  })();

  const effectiveMatchDetails = booking.matchDetails || matchDetailsProp || {};
  const ballTypeRaw = effectiveMatchDetails.ballType || '';
  const ballColorRaw = effectiveMatchDetails.ballColor || '';
  const oversLabel = effectiveMatchDetails.oversCount ?? 'N/A';
  const ballTypeLabel = ballTypeRaw || 'N/A';
  const ballColorLabel = ballColorRaw || '';

  const supportContact =
    contactPhone ||
    booking.venuePhone ||
    booking.supportPhone ||
    '';

  const mapUrl = googleMapUrl(
    booking.facilityLatitude ?? booking.latitude ?? activeFacility?.latitude,
    booking.facilityLongitude ?? booking.longitude ?? activeFacility?.longitude
  );

  const buildWhatsAppMessage = () => {
    const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
    const locationBlock = mapUrl ? `\n\n📍 *Location:* ${mapUrl}` : '';
    const discountLine = discountAmount > 0 ? `\n• Discount: ${inr(discountAmount)}` : '';
    const message =
`🏏 *Booking Confirmed!*

Thank you for choosing *${venueName}*. Your booking has been successfully confirmed. ✅

📅 *Date:* ${bookingDateLabel}
⏰ *Time:* ${startLabel} – ${endLabel}

💰 *Booking Details*
• Total Fees: ${inr(totalAmount)}${discountLine}
• Advance Paid: ${inr(advanceAmount)} ✅
• Balance Due: ${inr(balanceAmount)}${locationBlock}

We look forward to hosting you. Have a great game! 🏟️🏏`;

    return message;
  };

  const handleSendWhatsApp = async () => {
    const phone = normalizePhoneForWhatsApp(contactPhone);
    if (!phone) {
      setWhatsappStatus('Missing phone number for WhatsApp.');
      return;
    }
    const message = buildWhatsAppMessage();
    const whatsappUrl = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;

    try {
      window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
      setWhatsappStatus('WhatsApp opened with prefilled confirmation message.');
    } catch (error) {
      setWhatsappStatus('Unable to open WhatsApp. Please copy and send manually.');
    }
  };

  const handleConfirm = () => {
    setIsVisible(false);
    if (onConfirm) onConfirm();
    if (onClose) onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-ga-bg-surface rounded-lg shadow-ga-soft max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-ga-success p-6 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-ga-bg-surface rounded-full p-2">
                <CheckCircle className="h-8 w-8 text-ga-success" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-ga-text-on-primary">Booking Confirmed!</h2>
                <p className="text-ga-text-on-primary/90">Your booking has been successfully confirmed</p>
              </div>
            </div>
            <button
              onClick={handleConfirm}
              className="text-ga-text-on-primary hover:opacity-80 transition-opacity"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Booking Details */}
        <div className="p-6 space-y-6">
          {/* Service Information */}
          <div className="border-b border-ga-border-subtle pb-4">
            <h3 className="text-lg font-semibold text-ga-text-primary mb-3">Service Details</h3>
            <div className="space-y-2">
              <div className="flex items-center text-ga-text-primary">
                <span className="font-medium w-32">Service:</span>
                <span>{booking.serviceName || booking.groundName || 'Service'}</span>
              </div>
              {booking.venue && (
                <div className="flex items-center text-ga-text-primary">
                  <MapPin className="h-4 w-4 mr-2 text-ga-text-subtle" />
                  <span>{booking.venue}</span>
                </div>
              )}
              {booking.serviceType && (
                <div className="flex items-center text-ga-text-primary">
                  <span className="font-medium w-32">Type:</span>
                  <span className="px-2 py-1 bg-ga-primary-soft text-ga-primary rounded text-sm">
                    {booking.serviceType === 'cricket_match' ? 'Cricket Match' :
                     booking.serviceType === 'practice_net' ? 'Practice Net' :
                     booking.serviceType === 'open_net' ? 'Open Net' : booking.serviceType}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Date & Time */}
          <div className="border-b border-ga-border-subtle pb-4">
            <h3 className="text-lg font-semibold text-ga-text-primary mb-3">Schedule</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-center space-x-3">
                <Calendar className="h-5 w-5 text-ga-primary" />
                <div>
                  <p className="text-sm text-ga-text-subtle">Date</p>
                  <p className="font-medium text-ga-text-primary">
                    {booking.date ? (() => {
                      try {
                        // Handle ISO date strings
                        if (booking.date.includes('T')) {
                          return format(parseISO(booking.date), 'EEEE, MMMM dd, yyyy');
                        }
                        // Handle YYYY-MM-DD format
                        const dateObj = new Date(booking.date);
                        if (!isNaN(dateObj.getTime())) {
                          return format(dateObj, 'EEEE, MMMM dd, yyyy');
                        }
                        return booking.date;
                      } catch (e) {
                        return booking.date || 'N/A';
                      }
                    })() : 'N/A'}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Clock className="h-5 w-5 text-ga-primary" />
                <div>
                  <p className="text-sm text-ga-text-subtle">Time Slot</p>
                  <p className="font-medium text-ga-text-primary">
                    {composeSlotLabel(booking.slotName, booking.timeSlot)}
                  </p>
                </div>
              </div>
            </div>
            {booking.duration && (
              <div className="mt-4 flex items-center space-x-3">
                <Clock className="h-5 w-5 text-ga-primary" />
                <div>
                  <p className="text-sm text-ga-text-subtle">Duration</p>
                  <p className="font-medium text-ga-text-primary capitalize">{booking.duration}</p>
                </div>
              </div>
            )}
          </div>

          {/* Participants */}
          {(showTeamA || showTeamB || hasGuests) && (
            <div className="border-b border-ga-border-subtle pb-4">
              <h3 className="text-lg font-semibold text-ga-text-primary mb-3">Participants</h3>
              {(showTeamA || showTeamB) && (
                <div className="space-y-3">
                  {showTeamA && (
                    <div className="bg-ga-info/10 p-3 rounded-ga-md">
                      <p className="font-medium text-ga-text-primary mb-1">
                        Team A: {booking.teamDetails.teamA.name}
                      </p>
                      <p className="text-sm text-ga-text-muted">
                        Captain: {booking.teamDetails.teamA.captain}
                      </p>
                      {booking.teamDetails.teamA.players && (
                        <p className="text-xs text-ga-text-subtle mt-1">
                          {booking.teamDetails.teamA.players.length} players
                        </p>
                      )}
                    </div>
                  )}
                  {showTeamB && (
                    <div className="bg-ga-success/10 p-3 rounded-ga-md">
                      <p className="font-medium text-ga-text-primary mb-1">
                        Team B: {booking.teamDetails.teamB.name}
                      </p>
                      <p className="text-sm text-ga-text-muted">
                        Captain: {booking.teamDetails.teamB.captain}
                      </p>
                      {booking.teamDetails.teamB.players && (
                        <p className="text-xs text-ga-text-subtle mt-1">
                          {booking.teamDetails.teamB.players.length} players
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
              {hasGuests && (
                <div className="space-y-2">
                  <p className="text-sm text-ga-text-muted mb-2">
                    {booking.guestList.length} Guest{booking.guestList.length !== 1 ? 's' : ''}:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {booking.guestList.map((guest, index) => (
                      <span
                        key={index}
                        className="bg-ga-bg-surface-subtle text-ga-text-primary px-3 py-1 rounded-ga-full text-sm"
                      >
                        {guest.name || `Guest ${index + 1}`}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Contact Information */}
          <div className="border-b border-ga-border-subtle pb-4">
            <h3 className="text-lg font-semibold text-ga-text-primary mb-3">Contact Information</h3>
            <div className="space-y-2">
              <div className="flex items-center space-x-3">
                <Users className="h-5 w-5 text-ga-text-subtle" />
                <span className="text-ga-text-primary">{contactName}</span>
              </div>
              {contactPhone && (
                <div className="flex items-center space-x-3">
                  <Phone className="h-5 w-5 text-ga-text-subtle" />
                  <span className="text-ga-text-primary">{contactPhone}</span>
                </div>
              )}
              {contactEmail && (
                <div className="flex items-center space-x-3">
                  <Mail className="h-5 w-5 text-ga-text-subtle" />
                  <span className="text-ga-text-primary">{contactEmail}</span>
                </div>
              )}
            </div>
          </div>

          {/* Pricing */}
          <div className="border-b border-ga-border-subtle pb-4">
            <h3 className="text-lg font-semibold text-ga-text-primary mb-3">Payment Details</h3>
            <div className="bg-ga-bg-surface-subtle p-4 rounded-lg space-y-2">
              {baseAmount > 0 && (
                <div className="flex justify-between items-center text-sm text-ga-text-muted">
                  <span>Base Amount:</span>
                  <span>₹{baseAmount.toFixed(2)}</span>
                </div>
              )}
              {discountAmount > 0 && (
                <div className="flex justify-between items-center text-sm text-ga-success">
                  <span>Discount ({booking.paymentDetails?.discountType === 'percentage' ? `${booking.paymentDetails?.discountAmount || discountAmount}%` : 'Amount'}):</span>
                  <span>- ₹{discountAmount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between items-center pt-2 border-t border-ga-border-subtle">
                <span className="text-ga-text-primary font-medium">Total Amount:</span>
                <span className="text-2xl font-bold text-ga-primary">
                  ₹{totalAmount.toFixed(2)}
                </span>
              </div>
              {advanceAmount > 0 && (
                <div className="flex justify-between items-center text-sm text-ga-text-muted pt-2">
                  <span>Advance Amount:</span>
                  <span>₹{advanceAmount.toFixed(2)}</span>
                </div>
              )}
              {balanceAmount > 0 && (
                <div className="flex justify-between items-center text-sm font-semibold text-ga-warning pt-2">
                  <span>Balance Amount (Pending):</span>
                  <span>₹{balanceAmount.toFixed(2)}</span>
                </div>
              )}
              {balanceAmount === 0 && advanceAmount > 0 && (
                <div className="flex justify-between items-center text-sm font-semibold text-ga-success pt-2">
                  <span>Status:</span>
                  <span>Fully Paid</span>
                </div>
              )}
              {(booking.paymentDetails?.paymentMode || booking.paymentMode) && (
                <div className="flex justify-between items-center text-xs text-ga-text-subtle pt-2">
                  <span>Payment Mode:</span>
                  <span className="capitalize">{booking.paymentDetails?.paymentMode || booking.paymentMode}</span>
                </div>
              )}
            </div>
          </div>

          {/* Special Requests */}
          {booking.specialRequests && (
            <div className="border-b border-ga-border-subtle pb-4">
              <h3 className="text-lg font-semibold text-ga-text-primary mb-3">Special Requests</h3>
              <p className="text-ga-text-primary bg-ga-bg-surface-subtle p-3 rounded-lg">
                {booking.specialRequests}
              </p>
            </div>
          )}

          {/* WhatsApp Confirmation Message */}
          <div className="bg-ga-success/10 border border-ga-success/30 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Phone className="h-5 w-5 text-ga-success mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-ga-text-primary mb-2">
                  WhatsApp Confirmation
                </p>
                <div className="bg-ga-bg-surface border border-ga-success/30 rounded-lg p-3 text-sm text-ga-text-primary font-mono whitespace-pre-wrap">
                  {buildWhatsAppMessage()}
                </div>
                {contactPhone && (
                  <p className="text-xs text-ga-success mt-2">
                    Ready to send to {contactPhone}
                  </p>
                )}
                {whatsappStatus && (
                  <p className="text-xs text-ga-success mt-2">{whatsappStatus}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-ga-bg-surface-subtle px-6 py-4 rounded-b-lg flex justify-end space-x-3">
          <button
            onClick={handleSendWhatsApp}
            className="btn-secondary"
          >
            Send WhatsApp
          </button>
          <button
            onClick={handleConfirm}
            className="btn-primary"
          >
            View My Bookings
          </button>
        </div>
      </div>
    </div>
  );
};

export default Notification;

